/**
 * Price-move attribution card for Ask AI (center answer + right rail).
 * Always compact: window facts, shock/grind chips, capped causes, no quote dump.
 */
import React, { useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { ShowChart, TrendingDown, TrendingUp } from '@mui/icons-material';

const ROLE_STYLES = {
    root_cause: { label: 'Root cause', color: 'var(--danger)', bg: 'rgba(220, 38, 38, 0.12)' },
    catalyst: { label: 'Catalyst', color: 'var(--warning)', bg: 'rgba(217, 119, 6, 0.12)' },
    amplifier: { label: 'Amplifier', color: 'var(--brand-2)', bg: 'var(--bg-3)' },
    unrelated: { label: 'Unrelated', color: 'var(--muted)', bg: 'var(--bg-3)' },
};

const formatPct = (value) => {
    if (value == null || Number.isNaN(Number(value))) return null;
    const n = Number(value);
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
};

const formatPx = (value) => {
    if (value == null || Number.isNaN(Number(value))) return null;
    return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const formatDay = (value) => {
    if (!value) return null;
    const day = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(day.getTime())) return String(value).slice(0, 10);
    return day.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const priceSourceLabel = (source) => {
    if (source === 'priceHistory') return 'Mongo price history';
    if (source === 'yfinance') return 'Yahoo Finance';
    return null;
};

const selectCauses = (causes) => {
    const list = Array.isArray(causes) ? causes : [];
    const roots = list.filter((c) => c.role === 'root_cause');
    const catalysts = list.filter((c) => c.role === 'catalyst').slice(0, 1);
    const amplifiers = list.filter((c) => c.role === 'amplifier').slice(0, 2);
    return [...roots, ...catalysts, ...amplifiers];
};

export default function PriceMovePanel({ priceMove }) {
    const shownCauses = useMemo(
        () => selectCauses(priceMove?.causes),
        [priceMove],
    );

    if (!priceMove) return null;

    const changePct = formatPct(priceMove.totalChangePct);
    const down = Number(priceMove.totalChangePct) < 0;
    const firstPx = formatPx(priceMove.firstClose);
    const lastPx = formatPx(priceMove.lastClose);
    const startLabel = formatDay(priceMove.startDate);
    const endLabel = formatDay(priceMove.endDate);
    const sourceLabel = priceSourceLabel(priceMove.priceSource);
    const shock = priceMove.shock;
    const grind = priceMove.grind;
    const shockLabel = shock?.date
        ? `Shock ${formatDay(shock.date)} ${formatPct(shock.return_pct) || ''}`.trim()
        : null;
    const grindLabel = grind?.from_date
        ? `After ${formatDay(grind.from_date)} ${formatPct(grind.change_pct) || ''}`.trim()
        : null;
    const unknownRoot = priceMove.guidanceChanged === false
        && !shownCauses.some((c) => c.role === 'root_cause');

    return (
        <Box
            data-testid="price-move-panel"
            sx={{
                mb: 1.5,
                p: 1.25,
                borderRadius: 2,
                border: '1px solid var(--border)',
                backgroundColor: 'var(--bg-3)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
                <ShowChart sx={{ fontSize: 16, color: 'var(--brand-2)' }} />
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: 'var(--text-1)', fontSize: '0.75rem' }}
                >
                    Price-move attribution
                </Typography>
                {changePct && (
                    <Chip
                        size="small"
                        icon={down
                            ? <TrendingDown sx={{ fontSize: 14 }} />
                            : <TrendingUp sx={{ fontSize: 14 }} />}
                        label={changePct}
                        sx={{
                            height: 22,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            backgroundColor: down ? 'rgba(220, 38, 38, 0.12)' : 'rgba(22, 163, 74, 0.12)',
                            color: down ? 'var(--danger)' : 'var(--success)',
                            '& .MuiChip-icon': { color: 'inherit' },
                        }}
                    />
                )}
            </Box>

            {(startLabel || firstPx) && (
                <Typography sx={{ color: 'var(--text-2)', fontSize: '0.72rem', mb: 0.75 }}>
                    {startLabel && endLabel ? `${startLabel} → ${endLabel}` : startLabel || endLabel}
                    {firstPx && lastPx ? ` · ${firstPx} → ${lastPx}` : ''}
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: shownCauses.length || unknownRoot ? 1 : 0 }}>
                {shockLabel && (
                    <Chip size="small" label={shockLabel} variant="outlined" sx={{ height: 22, fontSize: '0.66rem' }} />
                )}
                {grindLabel && (
                    <Chip size="small" label={grindLabel} variant="outlined" sx={{ height: 22, fontSize: '0.66rem' }} />
                )}
                {priceMove.currentPeriod && (
                    <Chip size="small" label={priceMove.currentPeriod} variant="outlined" sx={{ height: 22, fontSize: '0.66rem' }} />
                )}
                {priceMove.guidanceChanged === true && (
                    <Chip size="small" label="Guidance changed" color="warning" variant="outlined" sx={{ height: 22, fontSize: '0.66rem' }} />
                )}
                {unknownRoot && (
                    <Chip size="small" label="Unknown root cause" variant="outlined" sx={{ height: 22, fontSize: '0.66rem' }} />
                )}
                {priceMove.newsCount > 0 && (
                    <Chip size="small" label={`${priceMove.newsCount} company news`} variant="outlined" sx={{ height: 22, fontSize: '0.66rem' }} />
                )}
            </Box>

            {shownCauses.map((cause, idx) => {
                const role = ROLE_STYLES[cause.role] || ROLE_STYLES.amplifier;
                return (
                    <Box key={`${cause.role}-${idx}`} sx={{ mb: idx === shownCauses.length - 1 ? 0 : 0.75 }}>
                        <Chip
                            size="small"
                            label={role.label}
                            sx={{
                                height: 18,
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                mr: 0.75,
                                mb: 0.25,
                                backgroundColor: role.bg,
                                color: role.color,
                            }}
                        />
                        <Typography component="span" sx={{ color: 'var(--text-2)', fontSize: '0.7rem' }}>
                            {cause.summary}
                            {cause.confidence != null ? ` (${cause.confidence}%)` : ''}
                        </Typography>
                    </Box>
                );
            })}

            {sourceLabel && (
                <Typography sx={{ color: 'var(--muted)', fontSize: '0.64rem', mt: 1 }}>
                    Prices from {sourceLabel}
                </Typography>
            )}
        </Box>
    );
}
